import React, { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "../UI/Button";
import { Activity } from "../../types/tasks";
import { ActivityCard } from "./ActivityCard";
import { ActivityForm } from "./ActivityForm";

interface Props {
  taskId: string;
  activities: Activity[];
  onChange: (activities: Activity[]) => void;
  readonly?: boolean;
}

export const ActivityList: React.FC<Props> = ({
  taskId,
  activities,
  onChange,
  readonly = false,
}) => {
  const { t } = useTranslation("tasks");
  const [editing, setEditing] = useState<Activity | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // IDs that were already completed when the form first opened — these are
  // DB-persisted completions and cannot be undone via the UI.
  const [lockedIds, setLockedIds] = useState<ReadonlySet<string>>(new Set());
  const capturedRef = useRef(false);
  useEffect(() => {
    if (!capturedRef.current && activities.length > 0) {
      capturedRef.current = true;
      setLockedIds(new Set(activities.filter((a) => !!a.completedAt).map((a) => a.id)));
    }
  }, [activities]);

  const handleSave = (activity: Activity) => {
    const exists = activities.some((a) => a.id === activity.id);
    const next = exists
      ? activities.map((a) => (a.id === activity.id ? activity : a))
      : [...activities, activity];
    onChange(next);
    setEditing(null);
    setIsAdding(false);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm(t("activity.deleteConfirm"))) return;
    onChange(activities.filter((a) => a.id !== id));
    if (editing?.id === id) setEditing(null);
  };

  const handleToggleComplete = (a: Activity) => {
    const completedAt = a.completedAt ? (null as unknown as undefined) : new Date().toISOString();
    onChange(
      activities.map((x) =>
        x.id === a.id
          ? { ...x, completedAt, updatedAt: new Date().toISOString() }
          : x,
      ),
    );
  };

  return (
    <div className="space-y-3">
      {activities.length === 0 && !isAdding && (
        <div className="text-sm text-gray-400 py-2">
          {t("activity.empty")}
        </div>
      )}

      <div className="space-y-2">
        {activities.map((a) =>
          !readonly && editing?.id === a.id ? (
            <ActivityForm
              key={a.id}
              taskId={taskId}
              initial={editing}
              onSave={handleSave}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <ActivityCard
              key={a.id}
              activity={a}
              locked={lockedIds.has(a.id)}
              readonly={readonly}
              onEdit={() => !readonly && setEditing(a)}
              onDelete={() => handleDelete(a.id)}
              onToggleComplete={() => handleToggleComplete(a)}
            />
          ),
        )}
      </div>

      {!readonly && (
        isAdding ? (
          <ActivityForm
            taskId={taskId}
            onSave={handleSave}
            onCancel={() => setIsAdding(false)}
          />
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => {
              setEditing(null);
              setIsAdding(true);
            }}
          >
            {t("activity.add")}
          </Button>
        )
      )}
    </div>
  );
};
